import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  HoverCard, HoverCardTrigger, HoverCardContent,
} from '../ui/hover-card';
import {
  Plus, Send, Trash2, CheckCircle2, Clock, User, Users,
  Building2, FileQuestion, ChevronDown, ChevronUp,
  Timer, HardHat, Zap, Sparkles,
  FileText, Flame, EyeOff, Calendar, StickyNote, X,
  PackageCheck, Inbox, Wand2, MessageSquare, ShieldCheck, AlertTriangle,
  Check, ArrowRight, History as HistoryIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { ZoneHeader, ZONE_STYLE } from './ZoneHeader';
import MirSmartImportDialog from './MirSmartImportDialog';
import MirWorkflowStepper, { stageMeta, waitingOnMeta, MIR_STAGES } from './MirWorkflowStepper';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// ─── MIR status metadata ─────────────────────────────────────
// buckets:
//   - "active"  → still outstanding / waiting on someone
//   - "held"    → docs have been received & are held by the broker,
//                 but the pack hasn't been sent back to the lender yet
//   - "done"    → returned to lender / completed (out of active view)
const MIR_STATUS_META = {
  outstanding:            { label: 'Outstanding',            tone: 'slate',   icon: FileQuestion, bucket: 'active' },
  waiting_on_client:      { label: 'Waiting on Client',      tone: 'amber',   icon: User,         bucket: 'active' },
  waiting_on_derwent:     { label: 'Waiting on Derwent',     tone: 'blue',    icon: HardHat,      bucket: 'active' },
  waiting_on_third_party: { label: 'Waiting on Third Party', tone: 'purple',  icon: Building2,    bucket: 'active' },
  docs_with_broker:       { label: 'Docs Held by Broker',    tone: 'lime',    icon: PackageCheck, bucket: 'held' },
  ready_to_send:          { label: 'Ready to Send',          tone: 'teal',    icon: Zap,          bucket: 'held' },
  sent_to_lender:         { label: 'Returned to Lender',     tone: 'emerald_dark', icon: Send,    bucket: 'done' },
  completed:              { label: 'Completed',              tone: 'emerald_dark', icon: CheckCircle2, bucket: 'done' },
};

const STATUS_TONE = {
  slate:   { bg: '#f1f5f9', ink: '#0f172a', accent: '#64748b', border: '#e2e8f0', mutedInk: '#475569' },
  amber:   { bg: '#fef3c7', ink: '#78350f', accent: '#d97706', border: '#fcd34d', mutedInk: '#92400e' },
  blue:    { bg: '#dbeafe', ink: '#1e3a8a', accent: '#2563eb', border: '#93c5fd', mutedInk: '#1e40af' },
  purple:  { bg: '#f3e8ff', ink: '#581c87', accent: '#9333ea', border: '#d8b4fe', mutedInk: '#6b21a8' },
  teal:    { bg: '#ccfbf1', ink: '#134e4a', accent: '#0d9488', border: '#5eead4', mutedInk: '#0f766e' },
  indigo:  { bg: '#e0e7ff', ink: '#312e81', accent: '#4f46e5', border: '#a5b4fc', mutedInk: '#3730a3' },
  // "docs held by broker" — LIGHT GREEN
  lime:    { bg: '#ecfdf5', ink: '#064e3b', accent: '#22c55e', border: '#a7f3d0', mutedInk: '#166534' },
  // legacy emerald (used elsewhere)
  emerald: { bg: '#d1fae5', ink: '#064e3b', accent: '#059669', border: '#6ee7b7', mutedInk: '#047857' },
  // "returned to lender / completed" — DARK GREEN
  emerald_dark: { bg: '#064e3b', ink: '#ffffff', accent: '#022c22', border: '#065f46', mutedInk: '#a7f3d0' },
  rose:    { bg: '#ffe4e6', ink: '#881337', accent: '#e11d48', border: '#fda4af', mutedInk: '#9f1239' },
};

const ageDays = (iso) => {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const target = new Date(iso + (String(iso).length === 10 ? 'T23:59:59' : ''));
  const diff = target.getTime() - Date.now();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(String(iso).length === 10 ? `${iso}T00:00:00` : iso)
      .toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  } catch { return String(iso); }
};

// ─── Main component ──────────────────────────────────────────
function MIRChecklistImpl({ pack, onPackChange, step = 3 }) {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [showSmart, setShowSmart] = useState(false);
  const [openMirId, setOpenMirId] = useState(null);
  const [draft, setDraft] = useState({
    description: '', person_responsible: '', waiting_on: '',
    documents_required: '', due_date: '', internal_notes: '',
  });

  const mirs = pack.mirs || [];

  // Bucket every MIR by lifecycle stage:
  //   - outstanding  → still waiting on someone (top of list, urgent first)
  //   - held         → docs received, held by broker (light green, bottom of active list)
  //   - done         → returned to lender / completed (collapsible)
  const buckets = useMemo(() => {
    const outstanding = [], held = [], done = [];
    mirs.forEach((m) => {
      const status = m.completed ? 'completed' : (m.status || 'outstanding');
      const meta = MIR_STATUS_META[status] || MIR_STATUS_META.outstanding;
      if (meta.bucket === 'done' || m.completed) done.push(m);
      else if (meta.bucket === 'held') held.push(m);
      else outstanding.push(m);
    });
    // Sort outstanding: overdue first, then due-soon, then oldest first.
    const rank = (m) => {
      const dueIn = daysUntil(m.due_date);
      const age = ageDays(m.date_received || m.created_at) || 0;
      const isOverdue = dueIn !== null && dueIn < 0;
      const isDueSoon = dueIn !== null && dueIn >= 0 && dueIn <= 1;
      return (isOverdue ? -1000 : 0) + (isDueSoon ? -100 : 0) - age;
    };
    outstanding.sort((a, b) => rank(a) - rank(b));
    // Docs-held: most-recently-received on top of held bucket.
    held.sort((a, b) => new Date(b.docs_received_at || b.updated_at || 0) - new Date(a.docs_received_at || a.updated_at || 0));
    // Done: most recently returned first
    done.sort((a, b) => new Date(b.date_sent_back || b.sent_at || b.completed_at || b.updated_at || 0) - new Date(a.date_sent_back || a.sent_at || a.completed_at || a.updated_at || 0));
    return { outstanding, held, done };
  }, [mirs]);

  const total = mirs.length;
  const outstandingCount = buckets.outstanding.length;
  const heldCount = buckets.held.length;
  const doneCount = buckets.done.length;
  const activeCount = outstandingCount + heldCount;

  // "Urgent" is now defined solely as OVERDUE (per user brief). Overdue rows
  // pulse subtly and get a red tone. Only outstanding MIRs are urgent —
  // once docs are in the broker's hands the row is no longer "urgent".
  const urgentCount = useMemo(
    () => buckets.outstanding.filter(m => {
      const dueIn = daysUntil(m.due_date);
      return dueIn !== null && dueIn < 0;
    }).length,
    [buckets.outstanding]
  );

  const allSatisfiedBanner = mirs.length > 0
    && activeCount === 0
    && (pack.status === 'all_mirs_satisfied' || pack.status === 'back_with_lender');

  // ─── API ───────────────────────────────────────────────────
  const addMir = async () => {
    if (!draft.description.trim()) {
      toast.error('MIR description is required');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}/mirs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(draft),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      onPackChange?.(updated);
      setDraft({ description: '', person_responsible: '', waiting_on: '',
                 documents_required: '', due_date: '', internal_notes: '' });
      setShowAdd(false);
      toast.success('MIR added');
    } catch (e) {
      toast.error(`Failed to add MIR — ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const patchMir = async (mirId, patch) => {
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}/mirs/${mirId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      onPackChange?.(updated);
      return updated;
    } catch (e) {
      toast.error(`Update failed — ${e.message}`);
    }
  };

  // Transition a MIR to a new stage (with optional reassign + waiting_on).
  const transitionStage = async (mirId, payload) => {
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}/mirs/${mirId}/stage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      onPackChange?.(updated);
      return updated;
    } catch (e) {
      toast.error(`Stage transition failed — ${e.message}`);
    }
  };

  // Post a comment against a MIR (per-MIR thread).
  const addMirComment = async (mirId, body) => {
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}/mirs/${mirId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      onPackChange?.(updated);
      return updated;
    } catch (e) {
      toast.error(`Comment failed — ${e.message}`);
    }
  };

  const deleteMir = async (mirId) => {
    if (!confirm('Delete this MIR?')) return;
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}/mirs/${mirId}`, {
        method: 'DELETE', headers: auth,
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      onPackChange?.(updated);
      setOpenMirId(null);
      toast.success('MIR deleted');
    } catch (e) {
      toast.error(`Delete failed — ${e.message}`);
    }
  };

  const markDocsReceived = async (mir) => {
    if (mir.status === 'docs_with_broker') {
      // toggle back to outstanding when re-clicked
      await patchMir(mir.id, { status: mir.waiting_on ? 'waiting_on_client' : 'outstanding' });
      toast.success('MIR moved back to Outstanding');
      return;
    }
    await patchMir(mir.id, { status: 'docs_with_broker', completed: false });
    toast.success('MIR marked as docs received — held by broker');
  };

  const markReturnedToLender = async (mir) => {
    const today = new Date().toISOString().slice(0, 10);
    await patchMir(mir.id, {
      status: 'sent_to_lender',
      completed: true,
      date_sent_back: mir.date_sent_back || today,
    });
    toast.success('MIR returned to lender');
  };

  const openMir = mirs.find(m => m.id === openMirId) || null;

  return (
    <div
      id="mir-section"
      className="rounded-2xl border-2 shadow-md overflow-hidden bg-white"
      style={{ borderColor: ZONE_STYLE.mirs.border, colorScheme: 'light' }}
      data-testid="pack-mir-checklist"
    >
      <ZoneHeader
        step={step}
        icon={FileQuestion}
        title="MIR Overview"
        subtitle="Lender information requests — click a row for full detail"
        {...ZONE_STYLE.mirs}
        testid="pl-mirs-header"
        right={
          <>
            <div className="hidden sm:flex items-center gap-1.5" data-testid="mir-summary-chips">
              <SummaryChip testid="mir-chip-outstanding" tone={outstandingCount ? 'slate' : 'slate'}  icon={Inbox}        label="Outstanding" count={outstandingCount} activeOverride={outstandingCount > 0} />
              <SummaryChip testid="mir-chip-held"        tone={heldCount ? 'lime' : 'slate'}          icon={PackageCheck} label="Docs held"   count={heldCount} />
              <SummaryChip testid="mir-chip-done"        tone={doneCount ? 'emerald_dark' : 'slate'}  icon={CheckCircle2} label="Returned"    count={doneCount} />
              {urgentCount > 0 && (
                <SummaryChip testid="mir-chip-urgent" tone="rose" icon={Flame} label="Urgent" count={urgentCount} pulse />
              )}
            </div>
            <Button
              onClick={() => setShowSmart(true)}
              size="sm"
              className="text-white text-xs shrink-0"
              style={{ background: 'linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)' }}
              data-testid="mir-smart-import-toggle"
              title="Paste a lender email — Hey Derwent will split it into individual MIRs"
            >
              <Wand2 className="w-3.5 h-3.5 mr-1" /> Smart Import
            </Button>
            <Button onClick={() => setShowAdd(v => !v)} size="sm" variant="outline" className="text-xs shrink-0" data-testid="mir-add-toggle">
              <Plus className="w-3.5 h-3.5 mr-1" /> {showAdd ? 'Cancel' : 'Add MIR'}
            </Button>
          </>
        }
      />

      {/* All-satisfied banner */}
      {allSatisfiedBanner && (
        <div className="px-4 py-2.5 flex items-center gap-2.5" style={{ background: 'linear-gradient(90deg, #ecfdf5 0%, #d1fae5 100%)', borderTop: '1px solid #6ee7b7', borderBottom: '1px solid #6ee7b7' }} data-testid="mir-all-satisfied">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#059669' }}>
            <Sparkles className="w-4 h-4" style={{ color: '#ffffff' }} />
          </div>
          <div className="flex-1">
            <div className="text-[12.5px] font-black" style={{ color: '#064e3b' }}>All MIRs Satisfied — Back with Lender</div>
            <div className="text-[10.5px] font-semibold" style={{ color: '#047857' }}>
              MIR-assessment SLA restarted{pack.expected_response_date ? ` — expected response ${new Date(pack.expected_response_date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}` : ''}.
            </div>
          </div>
        </div>
      )}

      {/* ─── MIR list, grouped by bucket ────────────────── */}
      <div className="p-3 space-y-5">
        {total === 0 ? (
          <EmptyState onAdd={() => setShowAdd(true)} onSmart={() => setShowSmart(true)} />
        ) : (
          <>
            {outstandingCount > 0 && (
              <BucketGroup
                testid="mir-group-outstanding" tone="slate" icon={Inbox}
                title="Outstanding MIRs"
                subtitle="Waiting for docs or info to come in"
                mirs={buckets.outstanding} allMirs={mirs}
                team={pack.assigned_users || []}
                onOpen={setOpenMirId}
                onPatch={patchMir}
                onDocsReceived={markDocsReceived}
                onReturn={markReturnedToLender}
              />
            )}
            {heldCount > 0 && (
              <BucketGroup
                testid="mir-group-held" tone="lime" icon={PackageCheck}
                title="Docs received · held by broker"
                subtitle="Ready to be packaged and returned to the lender"
                mirs={buckets.held} allMirs={mirs}
                team={pack.assigned_users || []}
                onOpen={setOpenMirId}
                onPatch={patchMir}
                onDocsReceived={markDocsReceived}
                onReturn={markReturnedToLender}
              />
            )}
            {doneCount > 0 && (
              <BucketGroup
                testid="mir-group-done" tone="emerald_dark" icon={CheckCircle2}
                title="Returned to lender / completed"
                subtitle="Closed — with the lender for review"
                mirs={buckets.done} allMirs={mirs}
                team={pack.assigned_users || []}
                onOpen={setOpenMirId}
                onPatch={patchMir}
                onDocsReceived={markDocsReceived}
                onReturn={markReturnedToLender}
                collapsible
                collapsed={!showDone}
                onToggle={() => setShowDone(v => !v)}
              />
            )}
            {activeCount === 0 && doneCount > 0 && !allSatisfiedBanner && (
              <div className="rounded-lg p-3 flex items-center gap-2.5" style={{ background: '#ecfdf5', border: '1px solid #a7f3d0' }} data-testid="mir-all-clear">
                <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: '#059669' }} />
                <div>
                  <div className="text-[12.5px] font-black" style={{ color: '#064e3b' }}>All MIRs actioned — nothing outstanding</div>
                  <div className="text-[10.5px] font-semibold" style={{ color: '#047857' }}>Update the file status when the lender confirms receipt.</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add MIR form (collapsible) */}
      {showAdd && (
        <div className="px-4 py-3 border-t" style={{ borderColor: '#e2e8f0', background: '#f8fafc' }} data-testid="mir-add-form">
          <div className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#64748b' }}>Add new MIR</div>
          <div className="space-y-2">
            <Textarea value={draft.description} placeholder="MIR description — what did the lender ask for? *"
              onChange={e => setDraft({ ...draft, description: e.target.value })}
              rows={2} className="text-xs bg-white" data-testid="new-mir-desc" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <MicroField testid="new-mir-person" label="Person responsible" value={draft.person_responsible}
                onChange={v => setDraft({ ...draft, person_responsible: v })} placeholder="e.g. Emmanuel" />
              <MicroField testid="new-mir-waiting" label="Waiting on" value={draft.waiting_on}
                onChange={v => setDraft({ ...draft, waiting_on: v })} placeholder="Client / Employer" />
              <MicroField testid="new-mir-due" label="Due date" type="date" value={draft.due_date}
                onChange={v => setDraft({ ...draft, due_date: v })} />
              <MicroField testid="new-mir-docs" label="Documents required" value={draft.documents_required}
                onChange={v => setDraft({ ...draft, documents_required: v })} placeholder="e.g. 3 mo. bank stmts" />
            </div>
            <Input value={draft.internal_notes} onChange={e => setDraft({ ...draft, internal_notes: e.target.value })}
              placeholder="Internal notes (not visible to lender)" className="h-8 text-xs bg-white" />
            <div className="flex justify-end pt-1">
              <Button onClick={addMir} disabled={busy || !draft.description.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs" data-testid="add-mir-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add MIR
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      <MirDetailDialog
        mir={openMir}
        index={openMir ? mirs.indexOf(openMir) + 1 : 0}
        team={pack.assigned_users || []}
        currentUser={{ id: null, name: null }}
        onClose={() => setOpenMirId(null)}
        onPatch={patchMir}
        onDelete={deleteMir}
        onDocsReceived={markDocsReceived}
        onReturn={markReturnedToLender}
        onTransition={transitionStage}
        onAddComment={addMirComment}
      />

      {/* Smart Import dialog */}
      <MirSmartImportDialog
        open={showSmart}
        onClose={() => setShowSmart(false)}
        pack={pack}
        onPackChange={onPackChange}
      />
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────

// Compact chip used in the header instead of the 3 large bucket cards
function SummaryChip({ testid, tone, icon: Icon, label, count, pulse, activeOverride }) {
  const t = STATUS_TONE[tone] || STATUS_TONE.slate;
  const active = activeOverride !== undefined ? activeOverride : count > 0;
  return (
    <div
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border ${active && pulse ? 'animate-pulse' : ''}`}
      style={{
        background: active ? t.bg : '#f8fafc',
        borderColor: active ? t.border : '#e2e8f0',
      }}
    >
      <Icon className="w-3 h-3" style={{ color: active ? t.accent : '#94a3b8' }} />
      <span className="text-[9.5px] font-black uppercase tracking-wider" style={{ color: active ? t.mutedInk : '#94a3b8' }}>
        {label}
      </span>
      <span className="text-[11px] font-black tabular-nums" style={{ color: active ? t.ink : '#94a3b8' }}>
        {count}
      </span>
    </div>
  );
}

// A grouped set of MIR rows (Outstanding / Held / Done)
// Header is intentionally always slate/gray — the ROWS carry the colour
// so the group header stays quiet and doesn't compete visually.
function BucketGroup({ testid, tone, icon: Icon, title, subtitle, mirs, allMirs, team, onOpen, onPatch, onDocsReceived, onReturn, collapsible, collapsed, onToggle }) {
  const t = STATUS_TONE.slate;
  const accent = (STATUS_TONE[tone] || STATUS_TONE.slate).accent;  // used only for the icon square + count badge
  return (
    <div className="rounded-lg border overflow-hidden" data-testid={testid} style={{ borderColor: t.border, background: '#ffffff' }}>
      <button
        type="button"
        onClick={collapsible ? onToggle : undefined}
        className={`w-full flex items-center gap-2.5 px-3 py-1.5 ${collapsible ? 'hover:opacity-90' : 'cursor-default'}`}
        style={{ background: t.bg, borderBottom: collapsed ? 'none' : `1px solid ${t.border}` }}
        data-testid={`${testid}-header`}
      >
        <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: accent }}>
          <Icon className="w-3.5 h-3.5" style={{ color: '#ffffff' }} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="text-[12px] font-black flex items-center gap-1.5" style={{ color: t.ink }}>
            <span className="truncate">{title}</span>
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full px-1 text-[10px] font-black tabular-nums" style={{ background: accent, color: '#ffffff' }}>
              {mirs.length}
            </span>
          </div>
          <div className="text-[10px] font-semibold truncate" style={{ color: t.mutedInk }}>{subtitle}</div>
        </div>
        {collapsible && (collapsed
          ? <ChevronDown className="w-3.5 h-3.5 shrink-0" style={{ color: t.mutedInk }} />
          : <ChevronUp className="w-3.5 h-3.5 shrink-0" style={{ color: t.mutedInk }} />)}
      </button>
      {!collapsed && (
        <ul className="divide-y" style={{ borderColor: '#f1f5f9' }} data-testid={`${testid}-list`}>
          {mirs.map((m) => (
            <MirRow
              key={m.id}
              m={m}
              team={team}
              index={allMirs.indexOf(m) + 1}
              onOpen={() => onOpen(m.id)}
              onPatch={onPatch}
              onDocsReceived={onDocsReceived}
              onReturn={onReturn}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function EmptyState({ onAdd, onSmart }) {
  return (
    <div className="text-center py-8" data-testid="no-mirs">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-3" style={{ background: '#eef2ff' }}>
        <FileQuestion className="w-6 h-6" style={{ color: '#4f46e5' }} />
      </div>
      <div className="text-sm font-black" style={{ color: '#0f172a' }}>No MIRs yet</div>
      <div className="text-[11.5px] mt-1 max-w-md mx-auto" style={{ color: '#64748b' }}>
        Add the first Missing Information Request manually, or paste the lender email and let Hey Derwent split it into MIRs for you.
      </div>
      <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={onSmart}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all hover:opacity-90 text-white"
          style={{ background: 'linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)' }}
          data-testid="mir-empty-smart"
        >
          <Wand2 className="w-3.5 h-3.5" /> Smart Import from email
        </button>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all hover:bg-slate-100 border-2"
          style={{ borderColor: '#e2e8f0', color: '#334155' }}
          data-testid="mir-empty-add"
        >
          <Plus className="w-3.5 h-3.5" /> Add manually
        </button>
      </div>
    </div>
  );
}

// ─── Compact single-line MIR row ─────────────────────────────
function MirRow({ m, index, team, onOpen, onPatch, onDocsReceived, onReturn }) {
  const status = m.completed ? 'completed' : (m.status || 'outstanding');
  const meta = MIR_STATUS_META[status] || MIR_STATUS_META.outstanding;
  const t = STATUS_TONE[meta.tone] || STATUS_TONE.slate;
  const StatusIcon = meta.icon;

  const isHeld = status === 'docs_with_broker' || status === 'ready_to_send';
  const isDone = meta.bucket === 'done';

  const dueIn = daysUntil(m.due_date);
  const isOverdue = dueIn !== null && dueIn < 0 && !isDone && !isHeld;
  const isDueSoon = dueIn !== null && dueIn >= 0 && dueIn <= 1 && !isDone && !isHeld;

  const owner = m.person_responsible?.trim();
  const waitingOn = m.waiting_on?.trim();

  // Left status bar colour + row background:
  //   overdue → red bar + red row (pulsing)
  //   held    → green bar + light-green row
  //   done    → dark-green row (white text, super compressed)
  //   default → tone accent bar, white row
  const barColour  = isOverdue ? '#dc2626' : isHeld ? '#22c55e' : isDone ? '#022c22' : t.accent;
  const rowBg      = isOverdue ? '#fff1f2' : isHeld ? '#ecfdf5' : isDone ? '#064e3b' : '#ffffff';
  const rowText    = isDone ? '#ffffff' : '#0f172a';
  const mutedText  = isDone ? '#a7f3d0' : '#64748b';

  // ─── SUPER-COMPRESSED variant for completed / returned MIRs ────
  // Single-line micro row — takes ~24px height instead of ~44px.
  if (isDone) {
    return (
      <li
        className="relative flex items-center gap-2 pl-2.5 pr-2 py-1 cursor-pointer transition-colors hover:brightness-110"
        style={{ background: rowBg }}
        data-testid={`mir-item-${m.id}`}
        data-status={status}
        data-compressed="true"
      >
        <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: barColour }} />
        <span
          className="inline-flex items-center justify-center w-4 h-4 rounded text-[9px] font-black tabular-nums shrink-0"
          style={{ background: '#022c22', color: '#a7f3d0' }}
        >
          {index}
        </span>
        <button
          type="button"
          onClick={onOpen}
          className="flex-1 min-w-0 text-left"
          data-testid={`mir-open-${m.id}`}
        >
          <div
            className="text-[11px] font-semibold leading-none truncate line-through opacity-90"
            style={{ color: rowText }}
            data-testid={`mir-description-${m.id}`}
            title={m.description}
          >
            {m.description || '(no description)'}
          </div>
        </button>
        {owner && (
          <span className="hidden md:inline-flex items-center gap-1 text-[9.5px] font-semibold shrink-0" style={{ color: mutedText }} title={`Responsible: ${owner}`}>
            <User className="w-2.5 h-2.5" /> {owner}
          </span>
        )}
        {(m.date_sent_back || m.sent_at) && (
          <span className="inline-flex items-center gap-1 text-[9.5px] font-black uppercase tracking-wider tabular-nums shrink-0" style={{ color: '#a7f3d0' }} title="Returned to lender">
            <Send className="w-2.5 h-2.5" />
            {fmtDate(m.date_sent_back || m.sent_at)}
          </span>
        )}
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0 rounded-full text-[8.5px] font-black uppercase tracking-wider shrink-0"
          style={{ background: '#022c22', color: '#a7f3d0' }}
        >
          <CheckCircle2 className="w-2.5 h-2.5" /> Done
        </span>
      </li>
    );
  }

  return (
    <li
      className={`relative flex items-center gap-2.5 pl-3 pr-2 py-2 cursor-pointer transition-colors ${isOverdue ? 'animate-pulse-subtle' : ''} ${isDone ? '' : 'hover:brightness-95'}`}
      style={{ background: rowBg }}
      data-testid={`mir-item-${m.id}`}
      data-status={status}
      data-overdue={isOverdue ? 'true' : 'false'}
    >
      {/* Left status bar */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 ${isOverdue ? 'animate-pulse' : ''}`}
        style={{ background: barColour }}
      />

      {/* #N */}
      <span
        className="inline-flex items-center justify-center w-5 h-5 rounded-md text-[10px] font-black tabular-nums shrink-0"
        style={{
          background: isDone ? '#022c22' : isHeld ? '#22c55e' : t.accent,
          color: '#ffffff',
        }}
      >
        #{index}
      </span>

      {/* Description + hover preview — clicking opens the modal */}
      <HoverCard openDelay={200} closeDelay={80}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            onClick={onOpen}
            className="flex-1 min-w-0 text-left"
            data-testid={`mir-open-${m.id}`}
          >
            <div
              className={`text-[12.5px] font-black leading-tight truncate ${isDone ? 'line-through opacity-90' : ''}`}
              style={{ color: rowText }}
              data-testid={`mir-description-${m.id}`}
            >
              {m.description || <span className="italic opacity-60">(no description — click to add one)</span>}
            </div>
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          side="top"
          align="start"
          className="w-80 p-0 shadow-xl border-2"
          style={{ borderColor: t.border, background: '#ffffff', colorScheme: 'light' }}
          data-testid={`mir-hover-${m.id}`}
        >
          <div className="px-3 py-2 border-b flex items-center gap-1.5" style={{ background: t.bg, borderColor: t.border }}>
            <StatusIcon className="w-3.5 h-3.5" style={{ color: t.accent }} />
            <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: t.mutedInk }}>MIR #{index} · {meta.label}</span>
            {isOverdue && (
              <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest" style={{ background: '#e11d48', color: '#ffffff' }}>
                <Flame className="w-2.5 h-2.5" /> Urgent
              </span>
            )}
          </div>
          <div className="p-3 space-y-2">
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest mb-0.5" style={{ color: '#64748b' }}>What the lender asked for</div>
              <div className="text-[12px] font-semibold leading-snug" style={{ color: '#0f172a' }}>
                {m.description || <span className="italic text-slate-400">(no description)</span>}
              </div>
            </div>
            {m.documents_required && (
              <div>
                <div className="text-[9px] font-black uppercase tracking-widest mb-0.5" style={{ color: '#64748b' }}>Documents required</div>
                <div className="text-[11.5px]" style={{ color: '#334155' }}>{m.documents_required}</div>
              </div>
            )}
            <div className="pt-1 text-[10px] font-semibold" style={{ color: '#64748b' }}>
              Click to open full details.
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>

      {/* Compact meta chips — kept simple: assignee + stage + due date. */}
      <div className="hidden md:flex items-center gap-1 shrink-0">
        {m.assignee_name ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: isDone ? '#022c22' : '#f1f5f9', color: isDone ? '#a7f3d0' : '#334155' }} title={`Assigned to ${m.assignee_name}${m.assignee_role ? ` (${m.assignee_role.toUpperCase()})` : ''}`}>
            <span className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-black" style={{ background: isDone ? '#065f46' : '#4f46e5', color: '#ffffff' }}>
              {(m.assignee_name || '?').split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase()}
            </span>
            {m.assignee_name}
          </span>
        ) : owner ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: isDone ? '#022c22' : '#f1f5f9', color: isDone ? '#a7f3d0' : '#334155' }} title={`Responsible: ${owner}`}>
            <User className="w-2.5 h-2.5" style={{ color: mutedText }} />
            {owner}
          </span>
        ) : null}
        {m.stage && (() => {
          const sm = stageMeta(m.stage);
          const Icn = sm.icon;
          return (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ background: sm.softBg, color: sm.softFg }}
              title={sm.desc}
            >
              <Icn className="w-2.5 h-2.5" />
              {sm.short}
            </span>
          );
        })()}
        {m.waiting_on && (() => {
          const wm = waitingOnMeta(m.waiting_on, team);
          if (!wm) return null;
          return (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded"
              style={{ background: wm.softBg, color: wm.softFg }}
              title={`Waiting on ${wm.label}`}
            >
              <Clock className="w-2.5 h-2.5" />
              {wm.label}
            </span>
          );
        })()}
        {!isDone && m.due_date && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-black tabular-nums px-1.5 py-0.5 rounded"
            style={{
              background: isOverdue ? '#fee2e2' : isDueSoon ? '#fef3c7' : isHeld ? '#dcfce7' : '#f1f5f9',
              color: isOverdue ? '#991b1b' : isDueSoon ? '#92400e' : isHeld ? '#166534' : '#334155',
            }}
            title={`Due ${fmtDate(m.due_date)}`}
          >
            <Timer className="w-2.5 h-2.5" />
            {isOverdue ? `${Math.abs(dueIn)}d OVER` : isDueSoon ? (dueIn === 0 ? 'DUE' : '1d') : fmtDate(m.due_date)}
          </span>
        )}
      </div>

      {/* Two-stage action buttons — hidden for completed MIRs */}
      {!isDone && (
        <div className="flex items-center gap-1 shrink-0" data-testid={`mir-actions-${m.id}`}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDocsReceived(m); }}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-all hover:opacity-90"
            style={{
              background: isHeld ? '#f0fdf4' : '#22c55e',
              color:      isHeld ? '#166534' : '#ffffff',
              border:     isHeld ? '1px solid #86efac' : '1px solid transparent',
            }}
            title={isHeld ? 'Docs already marked received — click to move back to outstanding' : 'Mark docs received — held by broker'}
            data-testid={`mir-docs-in-${m.id}`}
          >
            <PackageCheck className="w-2.5 h-2.5" />
            <span className="hidden lg:inline">{isHeld ? 'Docs In' : 'Docs In'}</span>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onReturn(m); }}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-black text-white uppercase tracking-wider transition-all hover:opacity-90"
            style={{ background: '#065f46' }}
            data-testid={`mir-return-${m.id}`}
            title="Return to lender — marks the MIR complete and moves it to Returned"
          >
            <Send className="w-2.5 h-2.5" />
            <span className="hidden lg:inline">Return</span>
          </button>
        </div>
      )}
    </li>
  );
}

// ─── Full-detail modal ───────────────────────────────────────
function MirDetailDialog({ mir, index, team, onClose, onPatch, onDelete, onDocsReceived, onReturn, onTransition, onAddComment }) {
  const [local, setLocal] = useState(null);
  const [tab, setTab] = useState('overview'); // overview | comments | history
  const [commentDraft, setCommentDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [changesNote, setChangesNote] = useState('');

  React.useEffect(() => {
    setLocal(mir ? { ...mir } : null);
    setTab('overview');
    setCommentDraft('');
    setChangesNote('');
  }, [mir]);

  if (!mir || !local) return null;

  const status = local.completed ? 'completed' : (local.status || 'outstanding');
  const meta = MIR_STATUS_META[status] || MIR_STATUS_META.outstanding;
  const t = STATUS_TONE[meta.tone] || STATUS_TONE.slate;

  const dueIn = daysUntil(local.due_date);
  const isOverdue = dueIn !== null && dueIn < 0 && !local.completed;

  // Batch save on blur — send only the fields that actually changed
  const commit = async (field, value) => {
    if (mir[field] === value) return;
    setLocal(prev => ({ ...prev, [field]: value }));
    await onPatch(mir.id, { [field]: value });
  };

  const currentStage = local.stage || 'awaiting_docs';
  const sm = stageMeta(currentStage);
  const StageIcon = sm.icon;

  // Actions available at the current stage.
  const goStage = async (stage, extra = {}) => {
    await onTransition(mir.id, { stage, ...extra });
    onClose();
  };
  const setAssignee = async (user) => {
    await onPatch(mir.id, {
      assignee_id: user?.id || null,
      assignee_name: user?.name || null,
      assignee_role: user?.role || null,
    });
    setLocal((prev) => ({ ...prev, assignee_id: user?.id || null, assignee_name: user?.name || null, assignee_role: user?.role || null }));
  };
  const setWaitingOn = async (val) => {
    await onPatch(mir.id, { waiting_on: val });
    setLocal((prev) => ({ ...prev, waiting_on: val }));
  };
  const postComment = async () => {
    const body = commentDraft.trim();
    if (!body) return;
    setPosting(true);
    const updated = await onAddComment(mir.id, body);
    setPosting(false);
    setCommentDraft('');
    if (updated) {
      const nextMir = (updated.mirs || []).find((m) => m.id === mir.id);
      if (nextMir) setLocal({ ...nextMir });
    }
  };

  const comments = local.comments || [];
  const history = local.history || [];

  const nextActions = [];
  if (currentStage === 'awaiting_docs') {
    nextActions.push({ key: 'docs_with_csm', label: 'Docs received from client', variant: 'primary' });
  } else if (currentStage === 'docs_with_csm') {
    nextActions.push({ key: 'pending_credit_review', label: 'Submit to Credit for review', variant: 'primary' });
  } else if (currentStage === 'pending_credit_review') {
    nextActions.push({ key: 'credit_approved', label: 'Approve (Credit sign-off)', variant: 'primary' });
    nextActions.push({ key: 'changes_requested', label: 'Request changes', variant: 'danger' });
  } else if (currentStage === 'changes_requested') {
    nextActions.push({ key: 'pending_credit_review', label: 'Resubmit to Credit', variant: 'primary' });
  } else if (currentStage === 'credit_approved') {
    nextActions.push({ key: 'sent_to_lender', label: 'Return to lender', variant: 'primary' });
  }

  return (
    <Dialog open={!!mir} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl p-0 gap-0 overflow-hidden bg-white"
        style={{ colorScheme: 'light' }}
        data-testid="mir-detail-dialog"
      >
        {/* Header */}
        <DialogHeader className="p-0">
          <div
            className="px-5 py-3.5 flex items-start gap-3"
            style={{
              background: isOverdue ? 'linear-gradient(90deg, #fff1f2 0%, #ffe4e6 100%)' : sm.softBg,
              borderBottom: `1px solid ${isOverdue ? '#fda4af' : sm.color}`,
            }}
          >
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: isOverdue ? '#e11d48' : sm.color }}
            >
              <StageIcon className="w-5 h-5" style={{ color: '#ffffff' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: sm.softFg }}>
                  MIR #{index}
                </span>
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-black uppercase tracking-wider"
                  style={{ background: sm.color, color: '#ffffff' }}
                >
                  {sm.label}
                </span>
                {isOverdue && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-black uppercase tracking-wider animate-pulse"
                    style={{ background: '#e11d48', color: '#ffffff' }}
                  >
                    <Flame className="w-2.5 h-2.5" /> Urgent · {Math.abs(dueIn)}d overdue
                  </span>
                )}
              </div>
              <DialogTitle className="text-base font-black leading-snug mt-1" style={{ color: '#0f172a' }}>
                {local.description || <span className="italic text-slate-400">(no description)</span>}
              </DialogTitle>
              <DialogDescription className="text-[11px] font-semibold mt-0.5" style={{ color: sm.softFg }}>
                Added {local.created_at ? new Date(local.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'} by {local.created_by_name || '—'}
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-white/60 transition-colors shrink-0"
              data-testid="mir-modal-close"
            >
              <X className="w-4 h-4" style={{ color: sm.softFg }} />
            </button>
          </div>
        </DialogHeader>

        {/* Workflow stepper */}
        <div className="px-5 pt-3 pb-2 border-b bg-slate-50" style={{ borderColor: '#e2e8f0' }}>
          <MirWorkflowStepper
            currentStage={currentStage}
            onGo={(s) => goStage(s)}
            onRequestChanges={() => goStage('pending_credit_review')}
          />
        </div>

        {/* Tabs */}
        <div className="px-5 pt-2 flex items-center gap-1 border-b" style={{ borderColor: '#e2e8f0' }}>
          {[
            { id: 'overview', label: 'Overview', icon: FileQuestion },
            { id: 'comments', label: `Comments${comments.length ? ` (${comments.length})` : ''}`, icon: MessageSquare },
            { id: 'history',  label: `History${history.length ? ` (${history.length})` : ''}`,   icon: HistoryIcon },
          ].map((tt) => (
            <button
              key={tt.id}
              type="button"
              onClick={() => setTab(tt.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition-colors ${tab === tt.id ? 'text-indigo-700 border-b-2 border-indigo-600' : 'text-slate-500 hover:text-slate-800'}`}
              data-testid={`mir-tab-${tt.id}`}
            >
              <tt.icon className="w-3 h-3" />
              {tt.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 max-h-[55vh] overflow-y-auto">
          {tab === 'overview' && (
            <>
              {/* Assignee + Waiting-on */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ModalField label="Assigned to" icon={User} testid="mir-modal-assignee">
                  <Select
                    value={local.assignee_id || 'unassigned'}
                    onValueChange={(v) => {
                      if (v === 'unassigned') return setAssignee(null);
                      const u = team.find((x) => x.id === v);
                      setAssignee(u || null);
                    }}
                  >
                    <SelectTrigger className="h-9 text-xs" data-testid="mir-modal-assignee-trigger">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {(team || []).map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}{u.role ? ` · ${String(u.role).toUpperCase()}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {team && team.length === 0 && (
                    <div className="text-[10px] italic mt-1 text-amber-700">Add team members to the pack to enable assignment.</div>
                  )}
                </ModalField>
                <ModalField label="Waiting on" icon={Clock} testid="mir-modal-waiting-on">
                  <Select value={local.waiting_on || 'client'} onValueChange={setWaitingOn}>
                    <SelectTrigger className="h-9 text-xs" data-testid="mir-modal-waiting-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="csm">CSM (any)</SelectItem>
                      <SelectItem value="credit">Credit Team (any)</SelectItem>
                      <SelectItem value="broker">Broker (any)</SelectItem>
                      <SelectItem value="lender">Lender</SelectItem>
                      <SelectItem value="third_party">Third Party</SelectItem>
                      {(team || []).length > 0 && (
                        <div className="px-2 py-1 mt-1 border-t text-[10px] font-black uppercase tracking-widest text-slate-400">
                          Or specific team member
                        </div>
                      )}
                      {(team || []).map((u) => (
                        <SelectItem key={`u-${u.id}`} value={`user:${u.id}`}>
                          {u.name}{u.role ? ` · ${String(u.role).toUpperCase()}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </ModalField>
              </div>

              {/* What the lender asked for */}
              <ModalField
                label="What the lender asked for"
                icon={FileQuestion}
                testid="mir-modal-description"
              >
                <Textarea
                  value={local.description || ''}
                  onChange={(e) => setLocal({ ...local, description: e.target.value })}
                  onBlur={(e) => commit('description', e.target.value)}
                  rows={2}
                  placeholder="Describe exactly what the lender has requested…"
                  className="text-sm"
                  style={{ color: '#0f172a' }}
                  data-testid="mir-modal-desc-input"
                />
              </ModalField>

              {/* Documents required */}
              <ModalField
                label="Documents / information required"
                icon={FileText}
                testid="mir-modal-docs"
              >
                <Textarea
                  value={local.documents_required || ''}
                  onChange={(e) => setLocal({ ...local, documents_required: e.target.value })}
                  onBlur={(e) => commit('documents_required', e.target.value)}
                  rows={2}
                  placeholder="e.g. 3 months of bank statements, current payslip, latest ATO NOA"
                  className="text-xs"
                  style={{ color: '#0f172a' }}
                />
              </ModalField>

              {/* Date grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <ModalField label="Date requested" icon={Calendar} testid="mir-modal-date-received">
                  <Input type="date" value={local.date_received || ''}
                    onChange={(e) => setLocal({ ...local, date_received: e.target.value })}
                    onBlur={(e) => commit('date_received', e.target.value)}
                    className="h-9 text-xs" style={{ color: '#0f172a' }} />
                </ModalField>
                <ModalField label="Due date" icon={Timer} testid="mir-modal-due" accent={isOverdue ? '#e11d48' : undefined}>
                  <Input type="date" value={local.due_date || ''}
                    onChange={(e) => setLocal({ ...local, due_date: e.target.value })}
                    onBlur={(e) => commit('due_date', e.target.value)}
                    className="h-9 text-xs" style={{ color: '#0f172a' }} />
                </ModalField>
                <ModalField label="ETA to receive" icon={Clock} testid="mir-modal-eta" hint="Best guess based on chase-ups">
                  <Input type="date" value={local.estimated_receipt_date || ''}
                    onChange={(e) => setLocal({ ...local, estimated_receipt_date: e.target.value })}
                    onBlur={(e) => commit('estimated_receipt_date', e.target.value)}
                    className="h-9 text-xs" style={{ color: '#0f172a' }} />
                </ModalField>
              </div>

              {/* Internal notes — highlighted as internal-only */}
              <div
                className="rounded-xl border-2 p-3"
                style={{ background: '#fffbeb', borderColor: '#fcd34d' }}
                data-testid="mir-modal-internal-notes-block"
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <StickyNote className="w-3.5 h-3.5" style={{ color: '#92400e' }} />
                  <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: '#78350f' }}>Internal notes (freeform)</span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest" style={{ background: '#78350f', color: '#ffffff' }}>
                    <EyeOff className="w-2.5 h-2.5" /> Not visible to lender
                  </span>
                </div>
                <Textarea
                  value={local.internal_notes || ''}
                  onChange={(e) => setLocal({ ...local, internal_notes: e.target.value })}
                  onBlur={(e) => commit('internal_notes', e.target.value)}
                  rows={3}
                  placeholder="Reminders, follow-up attempts, client updates."
                  className="text-xs bg-white"
                  style={{ color: '#0f172a' }}
                  data-testid="mir-modal-notes"
                />
              </div>
            </>
          )}

          {tab === 'comments' && (
            <div className="space-y-3">
              {comments.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-8 px-4 text-center">
                  <MessageSquare className="w-6 h-6 mx-auto text-slate-300 mb-1.5" />
                  <p className="text-[12px] font-black text-slate-800">No comments yet</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Ask a teammate to review, request extra docs, or leave a note for the credit team.</p>
                </div>
              ) : (
                <ol className="space-y-2">
                  {comments.map((c) => (
                    <li key={c.id} className="rounded-xl border p-3 bg-white shadow-sm" style={{ borderColor: '#e2e8f0' }} data-testid={`mir-comment-${c.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black text-white" style={{ background: '#4f46e5' }}>
                          {(c.author_name || '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                        </span>
                        <span className="text-[12px] font-black text-slate-900">{c.author_name}</span>
                        {c.author_role && (
                          <span className="text-[9.5px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: '#e0e7ff', color: '#3730a3' }}>{c.author_role}</span>
                        )}
                        <span className="ml-auto text-[10px] text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : ''}</span>
                      </div>
                      <p className="text-[12.5px] leading-snug whitespace-pre-wrap text-slate-800">{c.body}</p>
                    </li>
                  ))}
                </ol>
              )}
              <div className="rounded-xl border p-3 bg-slate-50" style={{ borderColor: '#e2e8f0' }}>
                <Textarea
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  rows={2}
                  placeholder="Leave a note for the team…"
                  className="text-xs bg-white"
                  style={{ color: '#0f172a' }}
                  data-testid="mir-comment-input"
                />
                <div className="flex justify-end mt-2">
                  <Button size="sm" onClick={postComment} disabled={posting || !commentDraft.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs" data-testid="mir-comment-submit">
                    <MessageSquare className="w-3 h-3 mr-1" /> Post comment
                  </Button>
                </div>
              </div>
            </div>
          )}

          {tab === 'history' && (
            <div>
              {history.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-8 px-4 text-center">
                  <HistoryIcon className="w-6 h-6 mx-auto text-slate-300 mb-1.5" />
                  <p className="text-[12px] font-black text-slate-800">No history yet</p>
                </div>
              ) : (
                <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3">
                  {history.slice().reverse().map((h, i) => {
                    const to = stageMeta(h.to_stage);
                    const from = h.from_stage ? stageMeta(h.from_stage) : null;
                    return (
                      <li key={h.id || i} className="relative pl-5" data-testid={`mir-history-${i}`}>
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow-sm" style={{ background: to.color }}>
                          <to.icon className="w-3 h-3 text-white" />
                        </span>
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: to.softBg, color: to.softFg }}>{to.label}</span>
                          <span className="text-[11px] font-semibold text-slate-500">{h.ts ? new Date(h.ts).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : ''}</span>
                          {h.actor_name && <span className="text-[11px] font-semibold text-slate-700 flex items-center gap-1"><User className="w-2.5 h-2.5 text-slate-400" />{h.actor_name}</span>}
                        </div>
                        {from && (
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            <span className="line-through mr-1.5">{from.label}</span>
                            <ArrowRight className="w-2.5 h-2.5 inline mr-1" />
                            <span className="font-semibold text-slate-700">{to.label}</span>
                          </p>
                        )}
                        {h.note && (
                          <p className="text-[12px] leading-snug mt-1 text-slate-800">{h.note}</p>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}
        </div>

        {/* Footer actions — stage-based */}
        <div
          className="px-5 py-3 border-t flex items-center justify-between gap-2 flex-wrap"
          style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}
        >
          <button
            type="button"
            onClick={() => onDelete(mir.id)}
            className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider px-2.5 py-1.5 rounded-md hover:bg-rose-50 transition-colors"
            style={{ color: '#e11d48' }}
            data-testid="mir-modal-delete"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete MIR
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={onClose} className="text-xs" data-testid="mir-modal-cancel">
              Close
            </Button>
            {currentStage === 'pending_credit_review' && (
              <div className="flex items-center gap-2">
                <Input
                  value={changesNote}
                  onChange={(e) => setChangesNote(e.target.value)}
                  placeholder="Change-request note (optional)"
                  className="h-8 text-xs w-52"
                  data-testid="mir-changes-note"
                />
              </div>
            )}
            {nextActions.map((a) => (
              <Button
                key={a.key}
                size="sm"
                onClick={() => goStage(a.key, a.key === 'changes_requested' ? { note: changesNote } : {})}
                className={`text-xs text-white ${a.variant === 'danger' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                data-testid={`mir-action-${a.key}`}
              >
                {a.variant === 'danger' ? <AlertTriangle className="w-3 h-3 mr-1" /> : a.key === 'sent_to_lender' ? <Send className="w-3 h-3 mr-1" /> : a.key === 'credit_approved' ? <ShieldCheck className="w-3 h-3 mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                {a.label}
              </Button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ModalField({ label, icon: Icon, testid, hint, accent, children }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon className="w-3 h-3" style={{ color: accent || '#64748b' }} />}
        <label className="text-[9.5px] font-black uppercase tracking-widest" style={{ color: accent || '#64748b' }}>
          {label}
        </label>
      </div>
      {children}
      {hint && <div className="text-[10px] italic mt-1" style={{ color: '#94a3b8' }}>{hint}</div>}
    </div>
  );
}

function MicroField({ label, value, onChange, placeholder, type = 'text', testid }) {
  return (
    <div>
      <label className="text-[9px] font-bold uppercase tracking-wider mb-1 block" style={{ color: '#64748b' }}>
        {label}
      </label>
      <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} className="h-8 text-xs bg-white" data-testid={testid} style={{ color: '#0f172a' }} />
    </div>
  );
}

const MIRChecklist = MIRChecklistImpl;
export default MIRChecklist;
